import { useEffect, useRef, useState } from "react";
import { Sparkles, X, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Shows the active affiliate that's about to be credited at checkout.
 * Lets the customer clear it (or paste a fresh one) BEFORE they place
 * the order — so a stale cookie from a previous session can never silently
 * credit the wrong affiliate.
 *
 * Mounts on /cart and /checkout. Polls /api/session/affiliate which mirrors
 * the exact resolution chain the order endpoint uses, so what's displayed
 * here is GUARANTEED to be what the order will be attributed to.
 *
 * `prominent` mode (used on /checkout) keeps the override input always
 * visible with autocomplete by name/slug, and shows an unmissable red
 * warning when no affiliate is detected so the customer is prompted to
 * type in who referred them.
 */
export default function ActiveAffiliateWidget({ compact = false, prominent = false }) {
  const [state, setState] = useState({ loading: true, active: null, sources: null });
  const [override, setOverride] = useState("");
  const [matches, setMatches] = useState([]);
  const [openSuggestions, setOpenSuggestions] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/session/affiliate");
      setState({ loading: false, active: data.active || null, sources: data.sources || null });
    } catch {
      setState({ loading: false, active: null, sources: null });
    }
  };

  useEffect(() => { load(); }, []);

  // Debounced autocomplete: search affiliates by name/slug substring
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = override.trim();
    if (q.length < 1) { setMatches([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/affiliates/search?q=${encodeURIComponent(q)}`);
        setMatches(data.matches || []);
        setOpenSuggestions(true);
      } catch {
        setMatches([]);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [override]);

  const clear = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete("/session/affiliate");
      try {
        document.cookie = "tm_ref=; path=/; max-age=0; samesite=none; secure";
        localStorage.removeItem("tm_ref");
      } catch { /* ignore */ }
      toast.success("Affiliate cleared. No one will be credited for the next order.");
      await load();
    } catch {
      toast.error("Could not clear affiliate. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const applyRef = async (ref) => {
    const trimmed = (ref || "").trim();
    if (!trimmed) { toast.error("Type an affiliate name first"); return; }
    setBusy(true);
    try {
      await api.post("/session/affiliate", { ref: trimmed });
      try {
        const isHttps = window.location.protocol === "https:";
        const attrs = isHttps ? "samesite=none; secure" : "samesite=lax";
        document.cookie = `tm_ref=${encodeURIComponent(trimmed)}; path=/; max-age=${90 * 24 * 3600}; ${attrs}`;
        localStorage.setItem("tm_ref", JSON.stringify({ ref: trimmed, captured_at: new Date().toISOString() }));
      } catch { /* ignore */ }
      toast.success(`Affiliate set: ${trimmed}`);
      setOverride("");
      setMatches([]);
      setOpenSuggestions(false);
      await load();
    } catch {
      toast.error("Could not set affiliate. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) return null;

  const isStale = state.sources?.user_record_stale && !state.sources?.js_cookie && !state.sources?.httponly_cookie;
  const noAffiliate = !state.active;

  // Container styling: red warning when prominent + no affiliate; blue when an affiliate is active
  const containerCls = prominent && noAffiliate
    ? "border-2 border-[#FF2A00] bg-[#FFF5F2]"
    : state.active
      ? "border border-[#0033FF] bg-[#F0F4FF]"
      : "border border-neutral-300 bg-neutral-50";

  return (
    <div
      data-testid="active-affiliate-widget"
      className={`${containerCls} ${compact ? "p-3" : "p-4"} mb-4`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold mb-1 ${prominent && noAffiliate ? "text-[#FF2A00]" : "text-[#0033FF]"}`}>
            {prominent && noAffiliate ? <AlertTriangle className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
            {prominent && noAffiliate ? "Were you referred?" : "Active affiliate"}
          </div>
          {state.active ? (
            <div className="min-w-0">
              <code
                data-testid="active-affiliate-ref"
                className="font-mono text-sm font-bold break-all"
              >
                {state.active}
              </code>
              <div className="text-[11px] text-neutral-600 mt-0.5">
                This referrer will be credited the commission for your next order.
              </div>
            </div>
          ) : prominent ? (
            <div className="text-xs text-neutral-800 font-medium">
              If a friend or seller sent you here, type their name below so they get credit. Leave blank if you found us on your own.
            </div>
          ) : (
            <div className="text-xs text-neutral-600">
              No affiliate active. Your order will be organic (no commission paid).
            </div>
          )}
        </div>
        {state.active && (
          <button
            data-testid="active-affiliate-clear-btn"
            type="button"
            onClick={clear}
            disabled={busy}
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 border border-black text-xs font-semibold hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00] disabled:opacity-50"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {isStale && (
        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-[#B25E00] bg-[#FFF7E6] border border-[#FFCC66] px-2 py-1.5">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Stored from an old session and ignored at checkout. Click your most recent affiliate link to refresh.</span>
        </div>
      )}

      {/* Override input: prominent mode shows it always with autocomplete */}
      {prominent ? (
        <div className={`mt-3 ${state.active ? "pt-3 border-t border-neutral-300" : ""}`}>
          {state.active && (
            <label className="block text-xs font-bold text-black mb-1.5">
              Wrong affiliate? Enter the correct name below.
            </label>
          )}
          <form onSubmit={(e) => { e.preventDefault(); applyRef(override); }} className="relative">
            <div className="flex gap-2">
              <input
                data-testid="active-affiliate-input"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                onFocus={() => override.trim() && setOpenSuggestions(true)}
                onBlur={() => setTimeout(() => setOpenSuggestions(false), 150)}
                placeholder="Type their name or affiliate code"
                className="flex-1 h-10 px-3 border border-black rounded-none text-sm"
                autoComplete="off"
              />
              <button
                type="submit"
                data-testid="active-affiliate-set-btn"
                disabled={busy || !override.trim()}
                className="h-10 px-4 bg-[#0033FF] hover:bg-[#0022CC] text-white text-sm font-semibold disabled:opacity-50"
              >
                {state.active ? "Replace" : "Credit"}
              </button>
            </div>
            {openSuggestions && matches.length > 0 && (
              <ul
                data-testid="active-affiliate-suggestions"
                className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-auto bg-white border border-black shadow-lg"
              >
                {matches.map((m) => (
                  <li key={m}>
                    <button
                      type="button"
                      data-testid={`active-affiliate-suggestion-${m}`}
                      onMouseDown={(e) => { e.preventDefault(); applyRef(m); }}
                      className="w-full text-left px-3 py-2 font-mono text-sm hover:bg-[#F0F4FF] border-b border-neutral-100 last:border-b-0"
                    >
                      {m}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </form>
          <p className="mt-1.5 text-[11px] text-neutral-500 leading-snug">
            Type the affiliate's name and we'll suggest matches. They'll earn a commission on your order.
          </p>
        </div>
      ) : (
        <details className="mt-2">
          <summary className="text-[11px] text-neutral-500 cursor-pointer hover:text-black">
            Wrong affiliate? Paste a slug to override
          </summary>
          <form onSubmit={(e) => { e.preventDefault(); applyRef(override); }} className="flex gap-2 mt-2">
            <input
              data-testid="active-affiliate-input"
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder="e.g. glasses-abc123"
              className="flex-1 h-9 px-2 border border-black rounded-none font-mono text-xs"
            />
            <button
              type="submit"
              data-testid="active-affiliate-set-btn"
              disabled={busy || !override.trim()}
              className="h-9 px-3 bg-[#0033FF] hover:bg-[#0022CC] text-white text-xs font-semibold disabled:opacity-50"
            >
              Set
            </button>
          </form>
        </details>
      )}
    </div>
  );
}
